import React from 'react';
import { EntryDrawer } from '../../components/library/EntryDrawer';
import { InlineRename } from '../../components/templates/InlineRename';
import { RowMenu } from '../../components/templates/RowMenu';
import { SortableList } from '../../components/templates/SortableList';
import { Icon, GripIcon } from '../../components/templates/icons';
import type { DeficiencyEntry, EntrySeverity } from '../../api/deficiency-entries';

// Per-section card with sortable entry list. Includes EntryRow as a private
// helper since it only renders inside SectionCard. Lifted out of
// DeficiencyLibraryPage.tsx during the 2026-05-01 split.

// Keeps the patch prop well-typed without leaking the whole API module.
export function updateEntryPatchShape(_patch: {
  title?: string;
  description?: string | null;
  severity?: EntrySeverity;
  section_name?: string;
  component_name?: string | null;
  trade_tag?: string | null;
}) {
  return _patch;
}

interface SectionCardProps {
  sectionName: string;
  entries: DeficiencyEntry[];
  open: boolean;
  onToggle: () => void;
  expandedId: string | null;
  onExpand: (id: string) => void;
  onPatch: (id: string, patch: Parameters<typeof updateEntryPatchShape>[0]) => void;
  onRename: (id: string, title: string) => void;
  onMoveTo: (id: string, target: string) => void;
  onDelete: (entry: DeficiencyEntry) => void;
  onReorder: (ids: string[]) => void;
  onAdd: () => void;
  sections: string[];
}

export function SectionCard({
  sectionName,
  entries,
  open,
  onToggle,
  expandedId,
  onExpand,
  onPatch,
  onRename,
  onMoveTo,
  onDelete,
  onReorder,
  onAdd,
  sections,
}: SectionCardProps) {
  return (
    <div className="ts-card" style={{ marginTop: 12 }}>
      <button
        type="button"
        className="dl-starter-head"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="ts-card-head-label">{sectionName}</span>
        <span className="ts-card-head-meta">
          {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
          {open && entries.length > 1 ? ' · drag to reorder' : ''}
        </span>
        <span className="ts-chev" aria-hidden="true">
          <Icon name={open ? 'chevDown' : 'chev'} size={16} />
        </span>
      </button>
      {open && (
        <>
          {entries.length === 0 ? (
            <div className="dl-empty-row">No entries in this section yet.</div>
          ) : (
            <SortableList
              groupKey={`library-${sectionName}`}
              items={entries}
              onReorder={onReorder}
              renderItem={(entry, { dragHandleProps, state }) => (
                <EntryRow
                  entry={entry}
                  expanded={expandedId === entry.id}
                  dragState={state}
                  dragHandleProps={dragHandleProps}
                  onToggle={() => onExpand(entry.id)}
                  onRename={(title) => onRename(entry.id, title)}
                  onPatch={(patch) => onPatch(entry.id, patch)}
                  onDelete={() => onDelete(entry)}
                  onMoveTo={(target) => onMoveTo(entry.id, target)}
                  moveTargets={sections.filter((s) => s !== entry.sectionName)}
                />
              )}
            />
          )}
          <button
            type="button"
            className="ts-attr-add"
            style={{ borderTop: '1px solid var(--border-1)' }}
            onClick={onAdd}
          >
            <Icon name="plus" size={12} /> Add entry
          </button>
        </>
      )}
    </div>
  );
}

interface EntryRowProps {
  entry: DeficiencyEntry;
  expanded: boolean;
  dragState: 'resting' | 'dragging' | 'drop-target' | 'ghost';
  dragHandleProps: { draggable: true; onDragStart: (e: React.DragEvent) => void; onDragEnd: () => void };
  onToggle: () => void;
  onRename: (title: string) => void;
  onPatch: (patch: {
    title?: string;
    description?: string | null;
    severity?: EntrySeverity;
    component_name?: string | null;
    trade_tag?: string | null;
  }) => void;
  onDelete: () => void;
  onMoveTo: (target: string) => void;
  moveTargets: string[];
}

function EntryRow({
  entry,
  expanded,
  dragState,
  dragHandleProps,
  onToggle,
  onRename,
  onPatch,
  onDelete,
  onMoveTo,
  moveTargets,
}: EntryRowProps) {
  const cls = ['ts-row', 'dl-row'];
  if (dragState === 'dragging') cls.push('ts-row--dragging');
  if (dragState === 'drop-target') cls.push('ts-row--drop-target');
  if (expanded) cls.push('ts-row--on');

  const sev = entry.severity;
  const sevLabel =
    sev === 'safety_concern' ? 'Safety' : sev === 'recommendation' ? 'Rec.' : '—';
  const sevClass =
    sev === 'safety_concern'
      ? 'dl-sev-tag dl-sev-tag--safety'
      : sev === 'recommendation'
      ? 'dl-sev-tag dl-sev-tag--rec'
      : 'dl-sev-tag dl-sev-tag--none';

  const hasBadges = entry.componentName || entry.tradeTag;

  return (
    <>
      <div className={cls.join(' ')} onClick={onToggle} role="button" tabIndex={0}>
        <span className="ts-grip" aria-label="Drag to reorder" {...dragHandleProps}>
          <GripIcon size={14} />
        </span>
        <div className="dl-row-title" onClick={(e) => e.stopPropagation()}>
          <InlineRename
            value={entry.title}
            onCommit={onRename}
            font="sans"
            size={14}
            ariaLabel="Rename entry"
          />
          {hasBadges && (
            <div className="dl-row-badges">
              {entry.componentName && (
                <span className="dl-row-component">{entry.componentName}</span>
              )}
              {entry.tradeTag && (
                <span className="dl-row-trade" title={`Trade: ${entry.tradeTag}`}>
                  {entry.tradeTag}
                </span>
              )}
            </div>
          )}
        </div>
        <span className={sevClass}>{sevLabel}</span>
        <span className="dl-row-uses">
          {entry.useCount} {entry.useCount === 1 ? 'use' : 'uses'}
        </span>
        <span className="ts-chev" aria-hidden="true">
          <Icon name={expanded ? 'chevDown' : 'chev'} size={16} />
        </span>
        <div onClick={(e) => e.stopPropagation()}>
          <RowMenu
            items={[
              ...(moveTargets.length > 0
                ? (moveTargets.map((target) => ({
                    label: `Move to ${target}`,
                    icon: 'reset' as const,
                    onClick: () => onMoveTo(target),
                  })) as Array<{ label: string; icon: 'reset'; onClick: () => void }>)
                : []),
              ...(moveTargets.length > 0 ? (['separator'] as const) : []),
              { label: 'Delete', icon: 'trash', danger: true, onClick: onDelete },
            ]}
          />
        </div>
      </div>
      {expanded && <EntryDrawer entry={entry} onPatch={onPatch} onClose={onToggle} />}
    </>
  );
}
